<!-- src/views/Home.vue -->

<template>
    <TheHeader/>
    <BaseContainer> 
        <div class="order-container">
            <h1 class="order-title">Schedule a Pickup</h1>
            <p class="order-subtitle"></p>
            
            <form @submit.prevent="submitOrder" class="order-form">
                <div class="form-group">
                    <label for="clientName">Client Name *</label>
                    <input 
                        id="clientName"
                        v-model="formData.clientName"
                        type="text" 
                        required 
                        class="form-input"
                        placeholder="Enter your full name"
                    >
                </div>

                <div class="form-group">
                    <label for="pickupAddress">LOAD Pickup Address *</label>
                    <textarea 
                        id="pickupAddress"
                        v-model="formData.pickupAddress"
                        required 
                        class="form-input"
                        placeholder="Enter your pickup address"
                        rows="3"
                    ></textarea>
                </div>

                <div class="form-group">
                    <label for="email">Email *</label>
                    <input 
                        id="email"
                        v-model="formData.email"
                        type="email" 
                        required 
                        class="form-input"
                        placeholder="Enter your email address"
                    >
                </div>

                <div class="form-group">
                    <label>Select One of the Following For Disposal</label>
                    <div class="service-options">
                        <label class="service-option">
                            <input 
                                type="radio" 
                                v-model="formData.serviceType" 
                                value="logs" 
                                required
                            >
                            <span class="service-label">
                                <span class="service-name">Logs</span>
                                <span class="service-price">$600</span>
                            </span>
                        </label>
                        
                        <label class="service-option">
                            <input 
                                type="radio" 
                                v-model="formData.serviceType" 
                                value="brush" 
                                required
                            >
                            <span class="service-label">
                                <span class="service-name">Brush</span>
                                <span class="service-price">$600</span>
                            </span>
                        </label>
                        
                        <label class="service-option">
                            <input 
                                type="radio" 
                                v-model="formData.serviceType" 
                                value="stumps" 
                                required
                            >
                            <span class="service-label">
                                <span class="service-name">Stumps</span>
                                <span class="service-price">$1000</span>
                            </span>
                        </label>
                    </div>
                </div>

                <!-- <div class="form-group">
                    <label for="phone">Phone Number (Optional)</label>
                    <input 
                        id="phone"
                        v-model="formData.phone"
                        type="tel" 
                        class="form-input"
                        placeholder="Enter your phone number"
                    >
                </div>

                <div class="form-group">
                    <label for="notes">Additional Notes (Optional)</label>
                    <textarea 
                        id="notes"
                        v-model="formData.notes"
                        class="form-input"
                        placeholder="Any additional information or special requests"
                        rows="3"
                    ></textarea>
                </div> -->

                <button 
                    type="submit" 
                    class="submit-btn"
                    :disabled="isSubmitting"
                >
                    {{ isSubmitting ? 'Submitting...' : 'Submit Pickup' }}
                </button>
            </form>

            <div v-if="submitMessage" class="submit-message" :class="submitMessageType">
                {{ submitMessage }}
            </div>
        </div>
    </BaseContainer>
    <TheFooter/>
</template>

<script setup lang="ts">
import { ref, reactive } from 'vue';
import TheHeader from '../components/TheHeader.vue';
import TheFooter from '../components/TheFooter.vue';
import BaseContainer from '../components/BaseContainer.vue';
import { sheetdbService } from '../services/sheetdbService';

interface FormData {
    clientName: string;
    pickupAddress: string;
    email: string;
    serviceType: string;
    phone: string;
    notes: string;
}

const isSubmitting = ref(false);
const submitMessage = ref('');
const submitMessageType = ref('');

const formData = reactive<FormData>({
    clientName: '',
    pickupAddress: '',
    email: '',
    serviceType: '',
    phone: '',
    notes: ''
});

const submitOrder = async (): Promise<void> => {
    isSubmitting.value = true;
    submitMessage.value = '';
    
    try {
        // Submit to SheetDB
        const result = await sheetdbService.submitFormData(formData);
        console.log('RESULT: ', result);
        if (result.success) {
            submitMessage.value = result.message;
            submitMessageType.value = 'success';
            
            // Reset form
            Object.assign(formData, {
                clientName: '',
                pickupAddress: '',
                email: '',
                serviceType: '',
                phone: '',
                notes: ''
            });
        } else {
            submitMessage.value = result.message;
            submitMessageType.value = 'error';
        }
    } catch (error) {
        console.error('Error submitting order:', error);
        submitMessage.value = 'Error submitting order. Please try again or contact us directly.';
        submitMessageType.value = 'error';
    } finally {
        isSubmitting.value = false;
    }
};
</script>

<style scoped>
.order-container {
    max-width: 600px;
    margin: 2rem auto;
    padding: 2rem;
    background: white;
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.order-title {
    color: var(--primary);
    font-size: 2.5rem;
    text-align: center;
    margin-bottom: 0.5rem;
}

.order-subtitle {
    text-align: center;
    color: #666;
    margin-bottom: 2rem;
    font-size: 1.1rem;
}

.order-form {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    background: var(--white);
    border-radius: 12px;
    padding: 2rem;
    margin-top: 2rem;
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.form-group label {
    font-weight: 600;
    color: #333;
    font-size: 1rem;
}

.form-input {
    padding: 0.75rem;
    border: 2px solid #e1e5e9;
    border-radius: 8px;
    font-size: 1rem;
    transition: border-color 0.3s ease;
    background: white;
}

.form-input:focus {
    outline: none;
    border-color: var(--primary);
}

.service-options {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.service-option {
    display: flex;
    align-items: center;
    padding: 1rem;
    border: 2px solid #e1e5e9;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s ease;
}

.service-option:hover {
    border-color: var(--primary);
    background: #fef7f5;
}

.service-option:hover .service-label {
    background: #fef7f5;
}

.service-option:hover input[type="radio"] {
    background: #fef7f5;
}

.service-option:hover .service-name,
.service-option:hover .service-price {
    background: #fef7f5;
}

.service-option:has(input[type="radio"]:checked) input[type="radio"] {
    background: #fef7f5;
}

.service-option:has(input[type="radio"]:checked) .service-name,
.service-option:has(input[type="radio"]:checked) .service-price {
    background: #fef7f5;
}

.service-option input[type="radio"] {
    margin-right: 1rem;
    transform: scale(1.2);
}

.service-option input[type="radio"]:checked + .service-label {
    color: var(--primary);
    font-weight: 600;
    background: #fef7f5;
}

.service-option input[type="radio"]:checked {
    accent-color: var(--primary);
}

.service-option:has(input[type="radio"]:checked) {
    border-color: var(--primary);
    background: #fef7f5;
}

.service-label {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    font-size: 1.1rem;
}

.service-name {
    font-weight: 500;
}

.service-price {
    font-weight: 700;
    color: var(--primary);
    font-size: 1.2rem;
}

.submit-btn {
    background: var(--primary);
    color: white;
    border: none;
    padding: 1rem 2rem;
    border-radius: 8px;
    font-size: 1.1rem;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.3s ease;
    margin-top: 1rem;
}

.submit-btn:hover:not(:disabled) {
    background: #d14a2a;
}

.submit-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.submit-message {
    margin-top: 1rem;
    padding: 1rem;
    border-radius: 8px;
    text-align: center;
    font-weight: 500;
}

.submit-message.success {
    background: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
}

.submit-message.error {
    background: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
}

@media (max-width: 768px) {
    .order-container {
        margin: 1rem;
        padding: 1.5rem;
    }
    
    .order-title {
        font-size: 2rem;
    }
    
    .service-label {
        flex-direction: column;
        align-items: flex-start;
        gap: 0.25rem;
    }
}
</style>